/**
 * MarkdownView — the one way descriptions DISPLAY anywhere in Avium.
 *
 * Renders stored markdown (see lib/richText.ts for the storage contract) with
 * brand-consistent hierarchy: H2/H3 headings, checklists, bullets, links, and
 * `avium:` images resolved to fresh signed URLs. Raw HTML is sanitized
 * (rehype-sanitize) — only the inline tags the editor emits (e.g. <u>) survive.
 */

import { useEffect, useState } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import rehypeRaw from 'rehype-raw';
import rehypeSanitize, { defaultSchema } from 'rehype-sanitize';
import { aviumUrlTransform, resolveFileSrc } from '../../lib/richText';

// Default schema + the underline tag the editor serializes; img must allow the
// avium: protocol so the resolver sees it.
const SCHEMA = {
  ...defaultSchema,
  tagNames: [...(defaultSchema.tagNames ?? []), 'u'],
  protocols: {
    ...defaultSchema.protocols,
    src: [...((defaultSchema.protocols as Record<string, string[]> | undefined)?.src ?? ['http', 'https']), 'avium'],
  },
};

function AviumImg({ src, alt }: { src?: string | Blob; alt?: string }) {
  const [url, setUrl] = useState('');
  useEffect(() => {
    let live = true;
    if (typeof src === 'string') void resolveFileSrc(src).then(u => { if (live) setUrl(u); });
    return () => { live = false; };
  }, [src]);
  if (!url) return <span className="inline-block text-[11px] text-ink-faint border border-dashed border-line rounded px-2 py-1">🖼 loading image…</span>;
  return <img src={url} alt={alt ?? ''} className="rounded-lg border border-line max-w-full my-1.5" />;
}

export default function MarkdownView({ markdown, className }: { markdown: string; className?: string }) {
  return (
    <div className={className}>
      <ReactMarkdown
        remarkPlugins={[remarkGfm]}
        rehypePlugins={[rehypeRaw, [rehypeSanitize, SCHEMA]]}
        urlTransform={aviumUrlTransform}
        components={{
          h1: ({ children }) => <h2 className="text-base font-bold text-ink mt-3 mb-1 first:mt-0">{children}</h2>,
          h2: ({ children }) => <h2 className="text-base font-bold text-ink mt-3 mb-1 first:mt-0">{children}</h2>,
          h3: ({ children }) => <h3 className="text-sm font-bold text-ink-muted mt-2.5 mb-1 first:mt-0">{children}</h3>,
          p:  ({ children }) => <p className="text-sm text-ink-muted leading-relaxed my-1">{children}</p>,
          ul: ({ children }) => <ul className="my-1 space-y-0.5 [&>li]:text-sm [&>li]:text-ink-muted list-disc pl-5 [&:has(input)]:list-none [&:has(input)]:pl-1">{children}</ul>,
          ol: ({ children }) => <ol className="my-1 space-y-0.5 [&>li]:text-sm [&>li]:text-ink-muted list-decimal pl-5">{children}</ol>,
          li: ({ children }) => <li className="leading-relaxed">{children}</li>,
          input: props => props.type === 'checkbox'
            ? <input type="checkbox" checked={!!props.checked} readOnly className="w-3.5 h-3.5 accent-[#0F766E] mr-1.5 align-middle" />
            : <input {...props} />,
          a: ({ href, children }) => (
            <a href={href} target="_blank" rel="noopener noreferrer" className="text-brand-primary dark:text-brand-accent font-medium underline underline-offset-2 hover:text-brand-primary-dark dark:hover:text-brand-primary-lighter">{children}</a>
          ),
          img: ({ src, alt }) => <AviumImg src={src} alt={alt} />,
          code: ({ children }) => <code className="bg-wash text-ink-muted rounded px-1 py-0.5 text-[12px] font-mono">{children}</code>,
          hr: () => <hr className="my-2 border-line" />,
          blockquote: ({ children }) => <blockquote className="border-l-2 border-teal-200 pl-3 text-ink-faint my-1.5">{children}</blockquote>,
        }}
      >
        {markdown}
      </ReactMarkdown>
    </div>
  );
}
